import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Mail, Send } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import './MailingsTab.css';

const fetchWithAuth = async (url, options = {}) => {
    const { method = 'GET', data } = options;
    const response = await axios({
        url,
        method,
        data,
        withCredentials: true
    });
    return response.data;
};

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const statusLabel = {
    success: 'Отправлено',
    partial: 'Частично',
    error: 'Ошибка'
};

const MailingsTab = () => {
    const queryClient = useQueryClient();
    const [subject, setSubject] = useState('');
    const [text, setText] = useState('');
    const [audience, setAudience] = useState('group');
    const [groupId, setGroupId] = useState('');
    const [userQuery, setUserQuery] = useState('');
    const [recipient, setRecipient] = useState(null);
    const [previewHtml, setPreviewHtml] = useState('');
    const [sending, setSending] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imageData, setImageData] = useState('');
    const [files, setFiles] = useState([]);
    const [resumingId, setResumingId] = useState(null);

    const handleResume = async (item) => {
        const warning = item.job_id
            ? 'Отправить следующую порцию из 80. Остальные пойдут по 80 в час.'
            : 'Список уже получивших не сохранён. Письма уйдут всем адресам этой рассылки, по 80 в час.';
        if (!window.confirm(warning)) return;
        setResumingId(item.id);
        try {
            const data = await fetchWithAuth(`${API_BASE_URL}/admin/nasledie/mailings/${item.id}/resume`, {
                method: 'POST'
            });
            if (data.pending > 0) {
                toast.success(`Отправлено ${data.sent}. Осталось ${data.pending}, дальше по 80 в час.`);
            } else {
                toast.success(`Отправлено ${data.sent}. Рассылка завершена.`);
            }
            queryClient.invalidateQueries('nasledieMailingsHistory');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Не удалось продолжить рассылку');
        } finally {
            setResumingId(null);
        }
    };

    const { data: meta, isLoading: metaLoading } = useQuery(
        'nasledieMailingsMeta',
        () => fetchWithAuth(`${API_BASE_URL}/admin/nasledie/mailings/meta`)
    );

    const { data: history, isLoading: historyLoading } = useQuery(
        'nasledieMailingsHistory',
        () => fetchWithAuth(`${API_BASE_URL}/admin/nasledie/mailings`)
    );

    const { data: userHits } = useQuery(
        ['nasledieMailingUsers', userQuery],
        () => fetchWithAuth(`${API_BASE_URL}/admin/nasledie/mailings/users?q=${encodeURIComponent(userQuery)}`),
        { enabled: audience === 'email' && userQuery.trim().length >= 2 && !recipient }
    );

    const selectedGroup = useMemo(
        () => (meta?.groups || []).find((group) => String(group.id) === String(groupId)),
        [meta, groupId]
    );

    const recipientCount = audience === 'group'
        ? (selectedGroup?.recipients || 0)
        : audience === 'consent'
            ? (meta?.consent_count || 0)
            : recipient ? 1 : 0;

    const canSend = subject.trim() && text.trim() && recipientCount > 0 && !sending;

    useEffect(() => {
        let cancelled = false;
        const handle = setTimeout(async () => {
            if (!text.trim() && !subject.trim()) {
                if (!cancelled) setPreviewHtml('');
                return;
            }
            try {
                const data = await fetchWithAuth(`${API_BASE_URL}/admin/nasledie/mailings/preview`, {
                    method: 'POST',
                    data: {
                        subject,
                        text,
                        image_data: imageData,
                        files: files.map((file) => ({ name: file.name, url: 'https://example.invalid/file' }))
                    }
                });
                if (!cancelled) setPreviewHtml(data.html || '');
            } catch (error) {
                if (!cancelled) setPreviewHtml('');
            }
        }, 400);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [subject, text, imageData, files]);

    const chooseUser = (user) => {
        setRecipient({ email: user.email, name: user.name || user.email });
        setUserQuery('');
    };

    const applyTypedEmail = () => {
        const email = userQuery.trim();
        if (!isEmail(email)) return;
        setRecipient({ email, name: email });
        setUserQuery('');
    };

    const handleSend = async (event) => {
        event.preventDefault();
        if (!canSend) return;

        const who = audience === 'group'
            ? `в группу «${selectedGroup?.title || ''}» (${recipientCount} получателей)`
            : audience === 'consent'
                ? `всем, у кого есть согласие на рассылку (${recipientCount} получателей)`
                : `на адрес ${recipient.email}`;

        const pace = recipientCount > 80
            ? ' Сразу уйдёт 80 писем, остальные — по 80 в час.'
            : '';
        if (!window.confirm(`Отправить письмо «${subject.trim()}» ${who}?${pace}`)) return;

        setSending(true);
        setLastResult(null);
        try {
            const body = new FormData();
            body.append('subject', subject.trim());
            body.append('text', text.trim());
            body.append('audience', audience);
            if (audience === 'group') body.append('group_id', groupId);
            if (audience === 'email') body.append('email', recipient.email);
            if (imageFile) body.append('image', imageFile);
            files.forEach((file) => body.append('files', file));
            const data = await fetchWithAuth(`${API_BASE_URL}/admin/nasledie/mailings/send`, {
                method: 'POST',
                data: body
            });
            setLastResult(data);
            if (data.pending > 0) {
                toast.success(`Отправлено ${data.sent}. Остальные ${data.pending} уйдут по 80 в час.`);
            } else if (data.failed) {
                toast.warn(`Отправлено: ${data.sent}. Не доставлено: ${data.failed}.`);
            } else {
                toast.success(`Письмо отправлено: ${data.sent}`);
            }
            queryClient.invalidateQueries('nasledieMailingsHistory');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Не удалось отправить рассылку');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="mailings-tab">
            <div className="mailings-heading">
                <Mail size={22} />
                <div>
                    <h2>Рассылки</h2>
                    <p>Письмо уходит в оформленном виде: шапка центра, тема, текст и контакты внизу.</p>
                </div>
            </div>

            <form className="mailings-layout" onSubmit={handleSend}>
                <div className="mailings-form">
                    <label className="mailings-field">
                        <span>Тема письма</span>
                        <input
                            value={subject}
                            onChange={(event) => setSubject(event.target.value)}
                            maxLength={200}
                            placeholder="Например: Напоминание о встрече"
                            required
                        />
                    </label>

                    <label className="mailings-field">
                        <span>Текст рассылки</span>
                        <textarea
                            value={text}
                            onChange={(event) => setText(event.target.value)}
                            placeholder="Вставьте текст. Пустая строка отделяет абзацы. Ссылки в тексте станут кликабельными."
                            rows={12}
                            required
                        />
                    </label>

                    <div className="mailings-field">
                        <span>Картинка</span>
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (!file) {
                                    setImageFile(null);
                                    setImageData('');
                                    return;
                                }
                                if (file.size > 5 * 1024 * 1024) {
                                    toast.error('Картинка больше 5 МБ');
                                    event.target.value = '';
                                    return;
                                }
                                setImageFile(file);
                                const reader = new FileReader();
                                reader.onload = () => setImageData(typeof reader.result === 'string' ? reader.result : '');
                                reader.readAsDataURL(file);
                            }}
                        />
                        {imageFile && (
                            <button
                                type="button"
                                className="mailings-clear-image"
                                onClick={() => {
                                    setImageFile(null);
                                    setImageData('');
                                }}
                            >
                                Убрать картинку
                            </button>
                        )}
                    </div>

                    <div className="mailings-field">
                        <span>Файлы</span>
                        <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.txt,.csv,.rtf,.odt,.ods,.odp"
                            onChange={(event) => {
                                const picked = Array.from(event.target.files || []);
                                event.target.value = '';
                                const allowed = picked.filter((file) => file.size <= 10 * 1024 * 1024);
                                if (allowed.length !== picked.length) toast.error('Файл больше 10 МБ не добавлен');
                                setFiles((prev) => {
                                    const next = prev.concat(allowed);
                                    if (next.length > 5) {
                                        toast.error('Можно прикрепить не больше 5 файлов');
                                        return next.slice(0, 5);
                                    }
                                    return next;
                                });
                            }}
                        />
                        <small className="mailings-muted">PDF, Word, Excel, PowerPoint, ZIP или текст. В письме будет ссылка на скачивание.</small>
                        {files.map((file) => (
                            <button
                                key={file.name + file.size + file.lastModified}
                                type="button"
                                className="mailings-clear-image"
                                onClick={() => setFiles((prev) => prev.filter((item) => item !== file))}
                            >
                                Убрать {file.name}
                            </button>
                        ))}
                    </div>

                    <fieldset className="mailings-audience">
                        <legend>Кому отправить</legend>
                        <div className="mailings-audience-grid">
                            <label className={audience === 'group' ? 'active' : ''}>
                                <input
                                    type="radio"
                                    name="audience"
                                    checked={audience === 'group'}
                                    onChange={() => setAudience('group')}
                                />
                                <strong>Группа</strong>
                                <small>Все, кто оставил заявку, кроме отклонённых</small>
                            </label>
                            <label className={audience === 'consent' ? 'active' : ''}>
                                <input
                                    type="radio"
                                    name="audience"
                                    checked={audience === 'consent'}
                                    onChange={() => setAudience('consent')}
                                />
                                <strong>Согласие на рассылку</strong>
                                <small>
                                    {metaLoading ? 'Считаем…' : `${meta?.consent_count || 0} адресов`}
                                </small>
                            </label>
                            <label className={audience === 'email' ? 'active' : ''}>
                                <input
                                    type="radio"
                                    name="audience"
                                    checked={audience === 'email'}
                                    onChange={() => setAudience('email')}
                                />
                                <strong>Один адрес</strong>
                                <small>Пользователь или почта</small>
                            </label>
                        </div>
                    </fieldset>

                    {audience === 'group' && (
                        <label className="mailings-field">
                            <span>Группа</span>
                            <select value={groupId} onChange={(event) => setGroupId(event.target.value)} required>
                                <option value="">{metaLoading ? 'Загрузка групп…' : 'Выберите группу'}</option>
                                {(meta?.groups || []).map((group) => (
                                    <option key={group.id} value={group.id}>
                                        {group.title}
                                        {group.direction_name ? ` · ${group.direction_name}` : ''}
                                        {group.region_name ? ` · ${group.region_name}` : ''}
                                        {` — ${group.recipients}`}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}

                    {audience === 'email' && (
                        <div className="mailings-field">
                            <span>Адрес или пользователь</span>
                            {recipient ? (
                                <div className="mailings-chip">
                                    <span>{recipient.name === recipient.email ? recipient.email : `${recipient.name} · ${recipient.email}`}</span>
                                    <button type="button" onClick={() => setRecipient(null)}>Убрать</button>
                                </div>
                            ) : (
                                <>
                                    <input
                                        value={userQuery}
                                        onChange={(event) => setUserQuery(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault();
                                                applyTypedEmail();
                                            }
                                        }}
                                        placeholder="Начните вводить имя или email"
                                    />
                                    {(userHits || []).length > 0 && (
                                        <ul className="mailings-suggest">
                                            {userHits.map((user) => (
                                                <li key={user.id}>
                                                    <button type="button" onClick={() => chooseUser(user)}>
                                                        <strong>{user.name || 'Без имени'}</strong>
                                                        <span>{user.email}</span>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {isEmail(userQuery) && (
                                        <button type="button" className="btn btn-secondary mailings-use-email" onClick={applyTypedEmail}>
                                            Отправить на {userQuery.trim()}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="mailings-send">
                        <p>Получателей: {recipientCount}</p>
                        <button type="submit" className="btn btn-primary" disabled={!canSend}>
                            <Send size={16} />
                            {sending ? 'Отправка…' : 'Отправить рассылку'}
                        </button>
                    </div>

                    {lastResult?.failures?.length > 0 && (
                        <ul className="mailings-failures">
                            {lastResult.failures.map((item) => (
                                <li key={item.email}>{item.email}: {item.message}</li>
                            ))}
                        </ul>
                    )}
                </div>

                <aside className="mailings-preview">
                    <h3>Как увидит получатель</h3>
                    {previewHtml ? (
                        <iframe title="Предпросмотр письма" sandbox="" srcDoc={previewHtml} />
                    ) : (
                        <div className="mailings-preview-empty">Тема и текст появятся здесь в виде письма.</div>
                    )}
                </aside>
            </form>

            <section className="mailings-history">
                <h3>Последние рассылки</h3>
                {historyLoading ? (
                    <p>Загрузка…</p>
                ) : !(history || []).length ? (
                    <p className="mailings-muted">Пока ничего не отправляли.</p>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Когда</th>
                                <th>Тема</th>
                                <th>Кому</th>
                                <th>Результат</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((item) => (
                                <tr key={item.id}>
                                    <td>{new Date(item.created_at).toLocaleString('ru-RU')}</td>
                                    <td>{item.subject}</td>
                                    <td>{item.audience_label || '—'}</td>
                                    <td>
                                        {statusLabel[item.status] || item.status}
                                        {item.sent != null ? ` · ${item.sent} из ${item.recipient_count}` : ''}
                                        {(item.pending > 0 || (item.status === 'partial' && !item.job_id)) && (
                                            <button
                                                type="button"
                                                className="mailings-resume"
                                                disabled={resumingId === item.id}
                                                onClick={() => handleResume(item)}
                                            >
                                                {resumingId === item.id ? 'Отправка…' : 'Доотправить'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
};

export default MailingsTab;
